import { AnimatePresence } from "framer-motion"
import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import H3 from "../atoms/typography/H3"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import SmallButton from "../atoms/buttons/SmallButton"
import { AddIcon } from "../atoms/icons/AddIcon"
import { ArrowDropDownIcon } from "../atoms/icons/ArrowDropDownIcon"
import { PersonRemoveIcon } from "../atoms/icons/PersonRemoveIcon"
import Modal from "../molecules/Modals/Modal"
import { useAddTerminalsToGroup } from "../../hooks/useAddTerminalsToGroup"
import Subtitle from "../atoms/typography/Subtitle"
import { useRemoveTerminalsFromGroup } from "../../hooks/useRemoveTerminalsFromGroup"
import { MediaQuery } from "../../utils/design-helpers"

import { useTranslation } from "react-i18next"
import { ArrowDropUpIcon } from "../atoms/icons/ArrowDropUpIcon"
import InviteTerminalToGroupsModalContent from "../molecules/Modals/InviteTerminalToGroupsModalContent"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"
import Body from "../atoms/typography/Body"

interface Props {
  terminalImgSrc: string
  terminalName: string
  terminalSerialNumber: string
  terminalId: string | number
  terminalDataPlan: string
  terminalSuborg: string
  sudopassword: string
  canEdit?: boolean
  open: boolean
  handleOpen: () => void
  groups?: Array<any>
}

const ContentContainer = styled.div<{ open: boolean }>`
  width: 100%;
  border-radius: ${(props) => props.theme.borders.softRadius};
  padding: 8px;
  background-color: ${(props) => (props.open ? props.theme.colors.greyLight : "transaprent")};

  :hover {
    background-color: ${(props) => props.theme.colors.greyLight};
  }
`

const Content = styled.div`
  padding: 15px;
`

const NameRoleSection = styled.div`
  display: flex;
  align-items: center;
`

const OrgRow = styled(Row)`
  padding: ${(props) => `${props.theme.spacings.medium} ${props.theme.spacings.large}`};
  background-color: ${(props) => props.theme.colors.greyLighter};
  border-radius: ${(props) => props.theme.borders.softRadius};
  min-height: 78px;
`

const Image = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
`

const RowContainer = styled(Row)`
  :hover .hiddentitle {
    ${MediaQuery.LG} {
      display: block;
    }
  }
`

const TitleHiddenDisplayOnHover = styled.div`
  display: none;
`

const EditTerminalSectionDropdown: React.FunctionComponent<Props> = ({
  terminalImgSrc,
  terminalDataPlan,
  terminalId,
  terminalName,
  terminalSerialNumber,
  canEdit,
  terminalSuborg,
  sudopassword,
  open,
  handleOpen,
  groups,
}) => {
  const { t } = useTranslation()
  const [inviteToGroupModalOpen, setInviteToGroupModalOpen] = React.useState(false)

  const onCompletedAddTerminalToGroup = () => {
    console.log("Added terminal to group")
    setInviteToGroupModalOpen(false)
  }
  const onErrorAddedTerminalToGroup = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, `error adding terminal ${terminalId} to group`)
  }
  const { addTerminalsToGroup } = useAddTerminalsToGroup({
    onCompleted: onCompletedAddTerminalToGroup,
    onError: (apolloError: ApolloError) => onErrorAddedTerminalToGroup(apolloError),
    terminalId: terminalId,
  })
  const onAddTerminalToGroup = async (groupId: number | string, organizationId: string) => {
    try {
      await addTerminalsToGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: organizationId,
          terminalIds: [{ id: terminalId }],
        },
      })
    } catch (error) {
      captureExceptionWithContext(
        error,
        `error adding terminal ${terminalId} to group ${groupId}, organizationId: ${organizationId}`,
      )
    }
  }

  const onCompletedRemoveFromGroup = () => {
    console.log("Removed terminal from group")
  }
  const onErrorRemoveFromGroup = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, `error removing terminal ${terminalId} from group`)
  }

  const { removeTerminalsFromGroup } = useRemoveTerminalsFromGroup({
    onCompleted: onCompletedRemoveFromGroup,
    onError: onErrorRemoveFromGroup,
    terminalId: terminalId,
  })

  const onClickRemoveFromGroup = async (groupId: number | string, orgId: number | string) => {
    try {
      await removeTerminalsFromGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: orgId,
          terminalIds: [{ id: terminalId }],
        },
      })
    } catch (error) {
      captureExceptionWithContext(
        error,
        `error removing terminal ${terminalId} from group ${groupId}, organizationId: ${orgId}`,
      )
    }
  }

  return (
    <>
      <ContentContainer open={open}>
        <div style={{ padding: "0 15px" }}>
          <RowContainer align="center" justify="between">
            <Col lg={2} md={6} xs={9}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Image src={terminalImgSrc} alt={terminalName} />
                <Spacer spacing={1} />
                <H3>{terminalName}</H3>
              </div>
            </Col>
            <Hidden xs md>
              <Col md={2} style={{ display: "flex", justifyContent: "center" }}>
                <div>
                  <TitleHiddenDisplayOnHover className="hiddentitle">
                    <Body textAlign="center">{t("serial_number")}</Body>
                  </TitleHiddenDisplayOnHover>
                  <H3 textAlign="center">{terminalSerialNumber}</H3>
                </div>
              </Col>
              <Col md={2} style={{ display: "flex", justifyContent: "center" }}>
                <div>
                  <TitleHiddenDisplayOnHover className="hiddentitle">
                    <Body textAlign="center">{t("suborganization")}</Body>
                  </TitleHiddenDisplayOnHover>
                  <H3 textAlign="center">{terminalSuborg}</H3>
                </div>
              </Col>
              <Col md={2} style={{ display: "flex", justifyContent: "center" }}>
                <div>
                  <TitleHiddenDisplayOnHover className="hiddentitle">
                    <Body textAlign="center">{t("data_plan")}</Body>
                  </TitleHiddenDisplayOnHover>
                  <H3 textAlign="center">{terminalDataPlan}</H3>
                </div>
              </Col>
              <Col md={2} style={{ display: "flex", justifyContent: "center" }}>
                <div>
                  <TitleHiddenDisplayOnHover className="hiddentitle">
                    <Body textAlign="center">{t("sudo_password")}</Body>
                  </TitleHiddenDisplayOnHover>
                  <H3 textAlign="center">{sudopassword}</H3>
                </div>
              </Col>
              <Col md={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                <SmallButton
                  title={open ? "Hide" : "Show"}
                  icon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  onClick={handleOpen}
                />
              </Col>
            </Hidden>
            <Visible xs md>
              <Col xs={2} md={1}>
                <div onClick={handleOpen}>{open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</div>
              </Col>
            </Visible>
          </RowContainer>
        </div>
        <SlideOutHeight isVisible={open}>
          <HorizontalSpacer spacing={1} />
          {canEdit && (
            <>
              <Row justify="center">
                <PrimaryButton
                  title="Add to group"
                  icon={<AddIcon />}
                  onClick={() => setInviteToGroupModalOpen(!inviteToGroupModalOpen)}
                />
              </Row>
              <HorizontalSpacer spacing={1} />
            </>
          )}

          <Content>
            <Visible xs md>
              <Body>
                {t("serial_number")}: {terminalSerialNumber}
              </Body>
              <HorizontalSpacer spacing={2} />
              <Body>
                {t("suborganization")}: {terminalSuborg}
              </Body>{" "}
              <HorizontalSpacer spacing={2} />
              <Body>
                {t("data_plan")}: {terminalDataPlan}
              </Body>{" "}
              <HorizontalSpacer spacing={2} />
              <Body>
                {t("sudo_password")}: {sudopassword}
              </Body>{" "}
              <HorizontalSpacer spacing={4} />
            </Visible>

            <Subtitle>Groups</Subtitle>
            <HorizontalSpacer spacing={2} />
            {groups?.map(
              (tg: {
                id: number | string
                name: string
                organization: { id: number | string }
                isAdminGroup: boolean
              }) => (
                <div key={tg.id}>
                  <OrgRow justify="between" align="center">
                    <NameRoleSection>
                      <H3>{tg.name}</H3>
                    </NameRoleSection>
                    {canEdit && !tg.isAdminGroup && (
                      <PrimaryButton
                        title={`Remove from ${tg.name}`}
                        icon={<PersonRemoveIcon />}
                        onClick={() => onClickRemoveFromGroup(tg.id, tg.organization.id)}
                      />
                    )}
                  </OrgRow>
                  <HorizontalSpacer spacing={0.5} />
                </div>
              ),
            )}
          </Content>
        </SlideOutHeight>
      </ContentContainer>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {inviteToGroupModalOpen && (
          <Modal
            open={inviteToGroupModalOpen}
            onClose={() => setInviteToGroupModalOpen(!inviteToGroupModalOpen)}
            closeIcon
          >
            <InviteTerminalToGroupsModalContent
              terminalName={terminalName}
              terminalId={terminalId}
              onCloseModal={() => setInviteToGroupModalOpen(!inviteToGroupModalOpen)}
              onClickInviteTerminalToGroup={onAddTerminalToGroup}
            />
          </Modal>
        )}
      </AnimatePresence>
    </>
  )
}

export default EditTerminalSectionDropdown
